import React, { useState } from "react";
import tailwindClassnames from "../common/TailwindClassnames";
import { useTranslation } from "react-i18next";
import { SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { DeploymentResponseDto } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";

type DeploymentsListProps = {
  listLoading: boolean;
  deployments: DeploymentResponseDto[];
  requestSort: Function;
  onSelectDeployment: (dep: DeploymentResponseDto) => void;
  onPageClicked: Function;
  take: number;
  offset: number;
  totalCount: number;
};

const DeploymentsList: React.FC<DeploymentsListProps> = ({
  listLoading,
  deployments,
  requestSort,
  onSelectDeployment,
  onPageClicked,
  take,
  offset,
  totalCount,
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortByColumn, setSortByColumn] = useState("deployment_name");

  const handleSort = (column: string) => {
    if (sortByColumn === column) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      requestSort(column, newDirection);
    } else {
      setSortByColumn(column);
      setSortDirection("asc");
      requestSort(column, "asc");
    }
  };

  const getArrowDirection = (column: string) => {
    return sortByColumn === column ? (
      sortDirection === "asc" ? (
        <SortedDown />
      ) : (
        <SortedUp />
      )
    ) : (
      <SortingArrows />
    );
  };

  /**
   * Header sort control: renders the column label with the appropriate sort
   * indicator and requests a re-sort on click. `column` is the API sort key.
   */
  const sortableHeader = (label: string, column: string) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 uppercase tracking-wider"
      onClick={() => handleSort(column)}
    >
      {label}
      {getArrowDirection(column)}
    </button>
  );

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}>
              {sortableHeader(t("Name"), "deployment_name")}
            </th>
            <th className={`${tailwindClassnames.tableCardHeadCell} min-w-36`}>
              {sortableHeader(t("Start Date"), "start_date")}
            </th>
            <th className={`${tailwindClassnames.tableCardHeadCell} min-w-36`}>
              {sortableHeader(t("End Date"), "end_date")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Location"), "location_name")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Sensor"), "sensor_name")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Number of Files"), "media_asset_count")}
            </th>
          </tr>
        </thead>
        <tbody>
          {listLoading ? (
            <tr>
              <td
                colSpan={6}
                className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("Loading")}...
              </td>
            </tr>
          ) : deployments.length > 0 ? (
            deployments.map((deployment) => {
              return (
                <tr
                  key={deployment.deployment_id}
                  className={tailwindClassnames.tableCardRow}
                  onClick={() => onSelectDeployment(deployment)}
                >
                  <td
                    className={tailwindClassnames.tableCardCellPrimary}
                    title={deployment.deployment_name ?? undefined}
                  >
                    {deployment.deployment_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={deployment.start_date || undefined}
                  >
                    {deployment.start_date}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={deployment.end_date || undefined}
                  >
                    {deployment.end_date}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={deployment.location_name ?? undefined}
                  >
                    {deployment.location_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={deployment.sensor_name ?? undefined}
                  >
                    {deployment.sensor_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={
                      deployment.media_asset_count != null
                        ? String(deployment.media_asset_count)
                        : undefined
                    }
                  >
                    {deployment.media_asset_count}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={6}
                className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("No results found")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Pagination
        showText={true}
        handlePageClick={onPageClicked}
        pageCount={Math.max(1, Math.ceil(totalCount / take))}
        resultStart={offset + 1}
        initialPage={Math.floor(offset / take)}
        resultEnd={offset + take < totalCount ? offset + take : totalCount}
        totalCount={totalCount}
        take={take}
        loading={listLoading}
        mainclassname={tailwindClassnames.tableCardFooter}
      />
    </div>
  );
};

export default DeploymentsList;
